// decision-auto-apply-rules.ts — V2-1 (spec V2 §4.1 "Luật auto (controller)").
//
// Hàm THUẦN: đủ dữ kiện thì trả "áp option X", thiếu bất kỳ điều kiện nào thì
// trả lý do (thành dòng "không auto vì …" + `decision_auto_skipped`). Điều kiện
// 1 (policy hiệu lực `auto`) do caller xét trước bằng
// decision-policy-authority-reader.ts; ở đây là 2–5:
//  2. options có id hợp lệ, `recommended_option_id` trỏ đúng một id, worker
//     khai đủ `boundary_claims`: `reversible: true`, 7 cờ còn lại đều `false`;
//  3. kiểm cứng token (không tin lời khai): mọi field string (đệ quy) của
//     option đề xuất + `summary` của result, không phân biệt hoa thường, theo
//     ranh giới `[A-Za-z0-9_]`; ticket kind ∈ {plan, testcase, implement, test,
//     fix, review}; chuỗi đã bị che (`REDACTED_PLACEHOLDER`) trong summary
//     hay BẤT KỲ option nào ⇒ ask (red-team R1: stream che TRƯỚC khi parse,
//     token ranh giới trùng giá trị secret đã thành `[REDACTED]`, không còn
//     gì để so — chỗ bị che là ranh giới chưa biết, fail-closed);
//  4. số quyết định auto của mission < trần (config V2 của run);
//  5. không có directive `pause`/`redirect` đang chờ áp.
import type { OptionIdLabelPair } from "./needs-decision-option-id-label-pairs.js";
import { BOUNDARY_CLAIM_FLAGS } from "./run-command-prompt-decision-options-contract-section.js";
import { REDACTED_PLACEHOLDER } from "./secret-match-intervals.js";

export const AUTO_DECIDABLE_TICKET_KINDS: readonly string[] = [
  "plan",
  "testcase",
  "implement",
  "test",
  "fix",
  "review",
];

export interface DecisionAutoRuleInput {
  pairs: OptionIdLabelPair[] | undefined;
  pairsProblem: string | undefined;
  recommendedOptionId: unknown;
  boundaryClaims: unknown;
  summary: string;
  ticketKind: string;
  tokens: readonly string[];
  autoDecisionsSoFar: number;
  maxAutoDecisions: number;
  pendingPauseOrRedirect: boolean;
}

export type DecisionAutoRuleVerdict =
  | { apply: true; option: OptionIdLabelPair; claims: Record<string, boolean> }
  | { apply: false; reason: string };

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Token đầu tiên khớp theo ranh giới từ `[A-Za-z0-9_]`, không phân biệt hoa thường. */
export function findBoundaryTokenMatch(
  texts: readonly string[],
  tokens: readonly string[],
): string | undefined {
  for (const token of tokens) {
    if (token.length === 0) continue;
    const re = new RegExp(`(?<![A-Za-z0-9_])${escapeRegExp(token)}(?![A-Za-z0-9_])`, "i");
    if (texts.some((text) => re.test(text))) return token;
  }
  return undefined;
}

/**
 * Mọi chuỗi trong một giá trị JSON (đệ quy qua mảng/object), GỒM CẢ key của
 * object — token ranh giới/marker che đặt ở key cũng phải bị thấy (red-team R6).
 */
export function collectStrings(value: unknown, out: string[] = []): string[] {
  if (typeof value === "string") out.push(value);
  else if (Array.isArray(value)) for (const v of value) collectStrings(v, out);
  else if (value !== null && typeof value === "object") {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out.push(k);
      collectStrings(v, out);
    }
  }
  return out;
}

function claimsProblem(claims: unknown): string | Record<string, boolean> {
  if (claims === null || typeof claims !== "object" || Array.isArray(claims)) {
    return "worker không khai boundary_claims";
  }
  const record = claims as Record<string, unknown>;
  const out: Record<string, boolean> = {};
  for (const flag of [...BOUNDARY_CLAIM_FLAGS, "reversible"]) {
    const value = record[flag];
    if (typeof value !== "boolean") return `boundary_claims thiếu cờ ${flag}`;
    out[flag] = value;
  }
  if (out.reversible !== true) return "boundary_claims.reversible ≠ true";
  const raised = BOUNDARY_CLAIM_FLAGS.filter((flag) => out[flag] === true);
  return raised.length > 0 ? `worker khai ${raised.join(", ")} = true` : out;
}

export function evaluateDecisionAutoRules(input: DecisionAutoRuleInput): DecisionAutoRuleVerdict {
  if (input.pairs === undefined) {
    return { apply: false, reason: input.pairsProblem ?? "options không có id hợp lệ" };
  }
  const option = input.pairs.find((p) => p.id === input.recommendedOptionId);
  if (option === undefined) {
    return { apply: false, reason: "recommended_option_id vắng hoặc không trỏ tới option nào" };
  }
  const claims = claimsProblem(input.boundaryClaims);
  if (typeof claims === "string") return { apply: false, reason: claims };
  if (!AUTO_DECIDABLE_TICKET_KINDS.includes(input.ticketKind)) {
    return { apply: false, reason: `ticket kind ${input.ticketKind} không thuộc loại được tự áp` };
  }
  const hit = findBoundaryTokenMatch(collectStrings([option.raw, input.summary]), input.tokens);
  if (hit !== undefined) {
    return { apply: false, reason: `lựa chọn/summary nhắc token ranh giới "${hit}"` };
  }
  const allOptions = input.pairs.map((p) => p.raw);
  if (collectStrings([allOptions, input.summary]).some((t) => t.includes(REDACTED_PLACEHOLDER))) {
    return {
      apply: false,
      reason: `lựa chọn/summary chứa chuỗi đã che ${REDACTED_PLACEHOLDER} (ranh giới không kiểm được)`,
    };
  }
  if (input.autoDecisionsSoFar >= input.maxAutoDecisions) {
    return {
      apply: false,
      reason: `đã tới trần ${input.maxAutoDecisions} quyết định tự áp của mission`,
    };
  }
  if (input.pendingPauseOrRedirect) {
    return { apply: false, reason: "có directive pause/redirect đang chờ áp" };
  }
  return { apply: true, option, claims };
}
